import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import api from "../lib/api";
import ServerStatus from "../components/ServerStatus";
import AuthBrandPanel from "../components/AuthBrandPanel";
import { Eye, EyeOff, ArrowRight, ArrowLeft, KeyRound, AlertTriangle, Loader2 } from "lucide-react";

function PasswordInput({ value, onChange, placeholder, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full rounded-xl border border-border bg-white px-4 py-3 pr-11 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all"
        {...props} />
      <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0F172A] transition-colors"
        aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set when the server rejects the token itself (expired / already used / invalid).
  // Unlike a fixable input error, the same link can never succeed again — so we swap
  // the form for the persistent recovery panel instead of a fleeting toast.
  const [linkError, setLinkError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — please log in");
      navigate("/login");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not reset password";
      // Token-related failures all reference the reset "link" — these can't be
      // retried, so surface the recovery panel. Other errors stay as a toast.
      if (/link/i.test(detail)) {
        setLinkError(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showRecovery = !token || linkError;

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* ══════════════ LEFT — FORM ══════════════ */}
      <div className="flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-[400px]">
          <Link to="/" className="lg:hidden mb-8 inline-block">
            <Logo className="h-9 w-auto" />
          </Link>

          <ServerStatus />

          {showRecovery ? (
            <>
              <div className="w-12 h-12 rounded-xl bg-[#E11D48]/10 flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-[#E11D48]" />
              </div>
              <h1 className="font-display font-black text-[2.2rem] leading-[1.1] tracking-tight text-[#0F172A] mb-2">
                {linkError ? "This link can't be used." : "Invalid reset link."}
              </h1>
              <p className="text-sm text-muted-foreground mb-8">
                {linkError
                  ? `${linkError} Reset links expire after 1 hour and can only be used once — request a fresh one to continue.`
                  : "This link is missing or malformed. Reset links expire after 1 hour and can only be used once — request a fresh one to continue."}
              </p>
              <Link to="/forgot-password"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-5 py-3 font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-all">
                Request a new link <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-[#0025D8]/10 flex items-center justify-center mb-5">
                <KeyRound className="w-6 h-6 text-[#0025D8]" />
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#0025D8] mb-2">RESET PASSWORD</div>
              <h1 className="font-display font-black text-[2.4rem] leading-[1.1] tracking-tight text-[#0F172A] mb-2">
                Set a new password.
              </h1>
              <p className="text-sm text-muted-foreground mb-8">Choose a secure password you don't use elsewhere.</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">New password</label>
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters" required minLength={8} data-testid="reset-password" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Confirm new password</label>
                  <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your password" required data-testid="reset-confirm" />
                </div>

                <button type="submit" disabled={submitting} data-testid="reset-submit"
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white py-3.5 font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>
                    : <>Update password <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}

          <Link to="/login" className="inline-flex items-center gap-1.5 mt-8 text-sm font-semibold text-muted-foreground hover:text-[#0025D8] transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Back to login
          </Link>
        </div>
      </div>

      {/* ══════════════ RIGHT — BRAND PANEL ══════════════ */}
      <AuthBrandPanel
        eyebrow="ACCOUNT RECOVERY"
        title="One step from getting back in."
        subtitle="Set a fresh password and your sessions, scores, and saved coaching are right where you left them."
      />
    </main>
  );
}
